import React, { MouseEventHandler, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "../common/SelectInput";
import SchemaForm from "../common/SchemaForm";
import { NewIntegration, DestinationType, Organization } from "../common/ObjectTypes";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { checkSchema } from "../common/SchemaFormTemplates/checkSchema";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { getButtonText } from "../common/TestActionFunctions";
import { Tooltip } from "../common/Tooltip";
import ReactGA4 from "react-ga4";
import { getEnvironment } from "../common/getEnvironment";

type DestinationModalProps = {
    currentMemberIsAdmin: boolean,
    errors: any,
    rsjfErrors: any,
    onToggleModal: MouseEventHandler,
    destination: NewIntegration,
    destinationType: DestinationType,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function
    destinationTypes: DestinationType[],
    onChange: Function,
    onChangeOrg: Function,
    onChangeType: Function,
    onChangeJSONForm: Function,
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    onGoToDetails: (destination_id: string) => void
}

const DestinationModal = ({
    currentMemberIsAdmin,
    errors,
    rsjfErrors,
    onToggleModal,
    destination,
    destinationType,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    destinationTypes,
    onChange,
    onChangeOrg,
    onChangeType,
    onChangeJSONForm,
    onSave,
    onGoToDetails
}: DestinationModalProps) => {
    const { t } = useTranslation();
    const [showAdvanced, setShowAdvanced] = useState(false);
    const shouldHideUrlField = isHiddenUrlDestinationType(destinationType.name);

    // Removed useScrollLock to allow scrolling
    const formInteractionStarted = useRef(false);

    // Track if a type has been selected
    const typeSelected = destinationType && destinationType.id && destinationType.id !== '0';

    const handleFormInteraction = () => {
        if (!formInteractionStarted.current) {
            const currentEnv = getEnvironment();
            ReactGA4.event('form_start', {
                form_name: 'new_destination_form',
                environment: currentEnv
            });
            formInteractionStarted.current = true;
        }
    };
    return (
        <div className="z-20 flex min-h-screen bg-very-light-gray dark:bg-off-black dark:text-white transition-colors duration-300">
            <div className="flex flex-col w-full min-h-screen my-4">
                <div className="pb-6">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-semibold mb-4 mx-6 sm:mb-8 dark:text-white transition-colors duration-300">{t("New Destination")}</h1>

                        <div className="flex flex-col justify-between h-screen">
                            <div className="flex flex-row">
                                <div className="w-2/3 bg-white dark:bg-off-black p-8">
                                    {/* STEP 1: DESTINATION TYPE */}
                                    <div className="px-4 sm:px-6">
                                        <div className="flex flex-col min-w-[400px]">
                                            <label>{t("Type")}</label>
                                            <SelectInput
                                                options={destinationTypes.map((type: DestinationType) => ({
                                                    label: type.name,
                                                    name: type.name,
                                                    description: type.description,
                                                    id: type.id,
                                                    value: type.id,
                                                    actions: type.actions
                                                }))}
                                                className={"basic-single text-black"}
                                                defaultValue={{
                                                    label: "Select",
                                                    value: 0
                                                }}
                                                isDisabled={false}
                                                isLoading={false}
                                                isClearable={false}
                                                isSearchable={true}
                                                isMulti={false}
                                                name={t("Type")}
                                                onFocus={handleFormInteraction}
                                                onChange={(e: any) => onChangeType(e)}
                                            />
                                        </div>
                                    </div>

                                    {/* STEP 2: NAME, CONFIGURATION, ENABLED */}
                                    {typeSelected && (
                                        <div className="h-full w-full mb-8 flex flex-col justify-between">
                                            <div className="px-2 mx-3">
                                                {/* Configuration section */}
                                                <div className="mt-8">
                                                    <div className="text-lg font-semibold text-secondary-gray dark:text-gray-300 transition-all duration-300 mb-4">
                                                        {t("Configuration")}
                                                    </div>
                                                    <div className="rounded border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-off-black p-5 transition-colors duration-300">
                                                        {/* Name */}
                                                        <div className="flex flex-col pb-4">
                                                            <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("Name")} *</label>
                                                            <input
                                                                id="name"
                                                                placeholder=""
                                                                className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-neutral-gray-700 dark:bg-dark-gray rounded rounded-sm px-2 dark:text-white transition-colors duration-300"
                                                                value={destination.name ? destination.name : ""}
                                                                onChange={(e) => onChange(e)}>
                                                            </input>
                                                            {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                                                        </div>
                                                        {/* owner: dropdown of all organizations */}
                                                        <div className="flex flex-col pb-4">
                                                            <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("Workspace")} *</label>
                                                            <SelectInput
                                                                options={organizations.map((org: Organization) => {
                                                                    return {
                                                                        label: org.name,
                                                                        value: org.id
                                                                    }
                                                                })}
                                                                className={"basic-single text-black"}
                                                                autoSelectSingleOption={true}
                                                                isDisabled={false}
                                                                isLoading={false}
                                                                isClearable={false}
                                                                isSearchable={true}
                                                                isMulti={false}
                                                                name={t("Workspace")}
                                                                onChange={(e: any) => onChangeOrg(e)}
                                                                onMenuScrollToBottom={loadMoreOrgs}
                                                                handleInputChange={orgInputChange}
                                                            />
                                                            {errors.owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                                                        </div>
                                                        {/* URL */}
                                                        {!shouldHideUrlField && <div className="flex flex-col">
                                                            <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("URL")} *</label>
                                                            <input
                                                                id="base_url"
                                                                placeholder=""
                                                                className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-neutral-gray-700 dark:bg-dark-gray rounded rounded-sm px-2 dark:text-white transition-colors duration-300"
                                                                value={destination.base_url ? destination.base_url : ""}
                                                                onChange={(e) => onChange(e)}>
                                                            </input>
                                                            {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                                        </div>}
                                                    </div>
                                                </div>
                                                {/* configuration */}
                                                <div className="pt-14">
                                                    {/* <label>Configurations</label> */}
                                                    {destinationType.actions.filter((item: any) => item.type !== "pull").map((item: any) => {
                                                        const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                                        const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                                                        return (
                                                            <div key={item.id} className={checkSchema(item) ? "pb-14" : "hidden"}>
                                                                {checkSchema(item) && (
                                                                    <>
                                                                        <div className="flex justify-between items-start w-full mb-4">
                                                                            <div className="text-2xl font-normal text-secondary-gray dark:text-gray-300 transition-all duration-300">{formTitle}</div>
                                                                            {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true && (
                                                                                <div className="flex flex-row items-center">
                                                                                    <button disabled className="opacity-100 bg-light-gray-button text-secondary-gray font-semibold rounded py-2 px-4 ml-1 mr-3 shadow">
                                                                                        {getButtonText(item.value, t)}
                                                                                    </button>
                                                                                    <Tooltip description={t("Save your destination first to enable this feature.")} position="right" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="rounded border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-off-black p-5 mb-8 transition-colors duration-300">
                                                                            <SchemaForm
                                                                                userIsAdmin={currentMemberIsAdmin}
                                                                                formTitle={''}
                                                                                schema={item.schema}
                                                                                ui_schema={uiSchema}
                                                                                onChange={(e: any) => onChangeJSONForm(e, item.id)}
                                                                                isEditable={true}
                                                                                errors={rsjfErrors[item.id]}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                {/* advanced configurations */}
                                                {shouldHideUrlField && <div className="pb-14">
                                                    <div className="mt-6 mb text-lg font-semibold text-secondary-gray dark:text-gray-lines cursor-pointer transition-colors duration-300" onClick={() => setShowAdvanced(!showAdvanced)}>
                                                        {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                                                    </div>
                                                    {showAdvanced &&
                                                        <div className="ml-8 flex flex-col">
                                                            <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("URL")}</label>
                                                            <input
                                                                id="base_url"
                                                                placeholder=""
                                                                className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-neutral-gray-700 dark:bg-dark-gray rounded rounded-sm px-2 dark:text-white transition-colors duration-300"
                                                                value={destination.base_url ? destination.base_url : ""}
                                                                onChange={(e) => onChange(e)}>
                                                            </input>
                                                            {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                                        </div>}
                                                </div>}
                                            </div>

                                        </div>

                                    )}
                                </div>
                                <div className="border border-gray-lines dark:border-secondary-gray ml-4 my-6 py-4 px-5 w-1/3 h-fit bg-white dark:bg-neutral-gray-900 hidden md:block transition-colors duration-300">
                                    <h3 className="text-off-black dark:text-white text-xl">{t('Learn more about {{technology}} connections', { technology: destinationType.name })}</h3>
                                    <p className="text-off-black dark:text-white text-base">{t("These articles in the knowledge base may be of assistance to you:")}</p>
                                    <ul className="list-disc pl-5 text-blue-basic dark:text-light-blue-300 text-s underline decoration-solid">
                                        <li>
                                            <a href="https://support.earthranger.com/connections/destinations" target="_blank" rel="noreferrer" className="text-blue-basic dark:text-light-blue-300">
                                                {t("Setting up a Destination in Gundi")}
                                            </a>
                                        </li>
                                        {destinationType.help_center_url && <li>
                                            <a href={destinationType.help_center_url} target="_blank" rel="noreferrer" className="text-blue-basic dark:text-light-blue-300">
                                                {destinationType.name} {t("Destination Set Up")}
                                            </a>
                                        </li>}
                                    </ul>
                                </div>
                            </div>
                            {/* submit and cancel button */}
                            <div className="sticky bottom-0 left-0 bg-white/50 dark:bg-dark-gray/50 backdrop-blur-sm flex flex-row py-3 px-4 sm:px-6 w-full transition-colors duration-300 z-50">
                                <button className={typeSelected ? "bg-dark-green text-white font-bold rounded px-7 py-2" : "bg-light-green text-neutral-gray-600 font-bold rounded px-7 py-2"}
                                    type="submit"
                                    disabled={!typeSelected}
                                    aria-disabled={!typeSelected}
                                    onClick={(e: any) => onSave(e, "save")}>
                                    {t("Save")}
                                </button>
                                <button className={typeSelected ? "bg-light-green text-dark-green font-bold rounded px-7 py-2 mx-3" : "bg-neutral-gray-50 text-neutral-gray-600 font-bold rounded px-7 py-2 mx-3"}
                                    type="submit"
                                    disabled={!typeSelected}
                                    aria-disabled={!typeSelected}
                                    onClick={(e: any) => onSave(e, "saveAndClose")}>
                                    {t("Save and Close")}
                                </button>
                                <button className="font-bold text-medium-gray dark:text-gray-lines rounded px-7 mx-2 transition-colors duration-300"
                                    type="button"
                                    onClick={onToggleModal}>
                                    {t("Cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DestinationModal;